import { List } from 'immutable';
import { STATE, GMI } from '../../../constants';
import { types } from '../../../utils/js/types';
import request from '../../request';
import replacement from '../../../services/gma/replacement';

const { REPLACEMENT } = STATE.UI;

/**
 * @private
 * @name setProtectionsFields
 * @desc Set protections fields on gmi.ui.replacement.extras.insurance
 * @param {object} args - Arguments
 * @param {object} args.extra - An extra object
 * @param {string} args.action - An user action (ACCEPT|DECLINE)
 * @returns {Function} Dispatched service action
 * @memberof actions.replacement.extras
 * @example 
 * store.dispatch(setProtectionsFields(args))
 */
const setProtectionsFields = ({ extra, action }) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentUIProtections = state.getIn(REPLACEMENT.EXTRAS.INSURANCE, []).toJS();

    const protectionsCopy = currentUIProtections.map(protection => {
      if(protection.code === extra.code) {
        protection.selected_quantity = action === "ACCEPT" ? 1 : 0;
        protection.action = action;
      }
      return protection;
    });

    dispatch({ 
      type: GMI.SET_GMI_STATE, 
      path: REPLACEMENT.EXTRAS.INSURANCE, 
      value: protectionsCopy
    });
  };
};

/**
 * @private
 * @name getExtraQuantity
 * @desc Util function to get selected quantity by user
 * @param {object} extra - An extra object
 * @param {string} action - An user action (add|remove)
 * @returns {Function} Dispatched service action
 * @memberof actions.replacement.extras
 * @example
 * getExtraQuantity(extra, quantity)
 */
function getExtraQuantity(extra, action) {
  const { selected_quantity: selectedQuantity, max_quantity: maxQuantity } = extra;
  switch (action) {
    case 'add':
      if (extra >= maxQuantity) return selectedQuantity;
      return selectedQuantity + 1;
    case 'remove':
      if (selectedQuantity === 0) return 0;
      return selectedQuantity - 1;
  }
}

/**
 * @private
 * @name setExtrasFields
 * @desc Set extras fields on gmi.ui.replacement.extras.equipment
 * @param {object} args - Arguments
 * @param {object} args.extra - An extra object
 * @param {string} args.action - An user action (add|remove)
 * @returns {Function} Dispatched service action
 * @memberof actions.replacement.extras
 * @example
 * store.dispatch(setExtrasFields(args))
 */
const setExtrasFields = ({ extra, action }) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentUIExtras = state.getIn(REPLACEMENT.EXTRAS.EQUIPMENT, []).toJS();

    const selectedQuantity = getExtraQuantity(extra, action);
  
    const extrasCopy = currentUIExtras.map(currentExtra => {
      if(currentExtra.code === extra.code) {
        currentExtra.selected_quantity = selectedQuantity;
      }
      return currentExtra;
    });
  
    dispatch({ 
      type: GMI.SET_GMI_STATE, 
      path: REPLACEMENT.EXTRAS.EQUIPMENT, 
      value: extrasCopy
    });
  };
};

/**
 * @private
 * @name setState
 * @desc Set the entire object on gmi.ui.replacement.extras
 * @param {object} value - Extra object with equipment and insurance inside
 * @returns {object} - Action
 * @memberof actions.replacement.extras
 * @example
 * store.dispatch(setState(value))
 */
const setState = (value) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentUIEquipment = state.getIn(REPLACEMENT.EXTRAS.EQUIPMENT, List([]))?.toJS();
    const currentUIInsurance = state.getIn(REPLACEMENT.EXTRAS.INSURANCE, List([]))?.toJS();

    if(currentUIEquipment?.length > 0) {
      // Update equipment from session and merge with action from UI
      // This is to avoid override action
      value?.equipment?.map(extra => {
        currentUIEquipment.filter(equipment => {
          if(extra?.code === equipment?.code && equipment?.action) {
            extra.action = equipment?.action;
          }
        });
      });
    }
    
    if(currentUIInsurance?.length > 0) {
      // Update insurance from session and merge with action from UI
      value?.insurance?.map(extra => {
        currentUIInsurance.filter(insurance => {
          if(extra?.code === insurance?.code && insurance?.action) {
            extra.action = insurance?.action;
          }
        });
      });
    }

    dispatch({ 
      type: GMI.SET_GMI_STATE, 
      path: REPLACEMENT.EXTRAS.PATH, 
      value
    });
  };
};

/**
 * @private
 * @name clearState
 * @desc Clear the entire object on gmi.ui.replacement.extras
 * @returns {object} - Action
 * @memberof actions.replacement.extras
 * @example
 * store.dispatch(clearState())
 */
const clearState = () => ({
  type: GMI.SET_GMI_STATE,
  path: REPLACEMENT.EXTRAS.PATH,
  value: {}
});

/**
 * @private
 * @name clearProtections
 * @desc Clear protections object on gmi.ui.replacement.extras.insurance
 * @returns {object} - Action
 * @memberof actions.replacement.extras
 * @example
 * store.dispatch(clearProtections())
 */
const clearProtections = () => ({
  type: GMI.SET_GMI_STATE,
  path: REPLACEMENT.EXTRAS.INSURANCE,
  value: []
});

/**
 * @private
 * @name clearExtras
 * @desc Clear extras object on gmi.ui.replacement.extras.equipment
 * @returns {object} - Action
 * @memberof actions.replacement.extras
 * @example
 * store.dispatch(clearExtras()) 
 */
const clearExtras = () => ({
  type: GMI.SET_GMI_STATE,
  path: REPLACEMENT.EXTRAS.EQUIPMENT,
  value: []
});

/**
 * The extra state object that has set and unset
 */
const state = {
  /**
   * @name set
   * @desc A global setter for gmi.ui.replacement.extras data
   * @param {string|null} [type] - The extras type (extras|protections|null)
   * @param {object} [args] - The arguments object
   * @returns {Function}
   * @memberof actions.replacement.extras
   * @example
   * 
   * // Set extras
   * store.dispatch(actions.replacement.state.set('extras', {}));
   * 
   * // Set protections
   * store.dispatch(actions.replacement.state.set('protections', {}));
   * 
   * // Set all
   * store.dispatch(actions.replacement.state.set(null, {}));
   * 
   */
  set(type = null, args = {}) {
    return (dispatch) => {
      if (types(type).isString) {
        if (type === 'extras') {
          dispatch(setExtrasFields(args));
        } else if (type === 'protections') {
          dispatch(setProtectionsFields(args));
        }
      } else {
        dispatch(setState(args));
      }
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for gmi.ui.replacement.extras data
   * @param {string|null} [type] - The extras type (extras|protections|null)
   * @returns {Function}
   * @memberof actions.replacement.extras
   * @example
   * 
   * // Unset extras
   * store.dispatch(actions.replacement.state.unset('extras'));
   * 
   * // Set protections
   * store.dispatch(actions.replacement.state.unset('protections'));
   * 
   * // Set all
   * store.dispatch(actions.replacement.state.unset(null));
   * 
   */
  unset(type = null) {
    return (dispatch) => {
      if (types(type).isString) {
        if (type === 'extras') {
          dispatch(clearExtras());
        } else if (type === 'protections') {
          dispatch(clearProtections());
        }
      } else {
        dispatch(clearState());
      }
    };
  }
};

/**
 * @name getExtrasState
 * @desc Get extras state function from gmi.ui.replacement.extras
 * @param {object} session - Session object
 * @memberof actions.replacement.extras
 * @returns {object} - Extras object
 * @example
 * store.dispatch(getExtrasState(session))
 */
function getExtrasState(session) {
  const extrasObject = session.getIn(["replacement", "ticket", "car_class_details", "vehicle_rates", 0, "extras"])?.toJS();
    
  if (!extrasObject) return {};

  return extrasObject;
}

/**
 * @name submitProtections
 * @desc Submit protections object from gmi.ui.replacement.extras.insurance
 * @memberof actions.replacement.extras
 * @returns {Function} - Dispatched service
 * @example
 * store.dispatch(actions.replacement.extras.submitProtections)
 */
function submitProtections() {
  return (dispatch, getState) => {
    const state = getState();
    
    const protections = state.getIn(REPLACEMENT.EXTRAS.INSURANCE).toJS();
    const payload = protections?.map(protection => {
      return {
        code: protection?.code,
        quantity: protection?.selected_quantity
      };
    });
    
    return dispatch(
      request({
        service: replacement.protections,
        data: { extras: [ ...payload ] }
      })
    );
  };
}

/**
 * @name submitExtras
 * @desc Submit extras object from gmi.ui.replacement.extras.equipment
 * @returns {Function} - Dispatched service
 * @memberof actions.replacement.extras
 * @example
 * store.dispatch(actions.replacement.extras.submitExtras)
 */
function submitExtras() {
  return (dispatch, getState) => {
    const state = getState();
      
    const extras = state.getIn(REPLACEMENT.EXTRAS.EQUIPMENT).toJS();
    const payload = extras?.map(extra => {
      return {
        code: extra?.code,
        quantity: extra?.selected_quantity
      };
    });
      
    return dispatch(
      request({
        service: replacement.extras,
        data: { extras: [ ...payload ] }
      })
    );
  };
}

export default {
  state,
  getExtrasState,
  submitProtections,
  submitExtras
};
